// ==========================================================================
// Global Name Modal - Shows whenever user.requires_name is true
// Placed at App level so it appears regardless of current route
// ==========================================================================

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser, useUpdateUserName } from '@/hooks/useUser';
import { z } from 'zod';

const nameSchema = z.string().trim().min(3, 'Name must be at least 3 characters');

export function RequireNameModal() {
  const { t } = useTranslation();
  const { data: user } = useUser();
  const updateName = useUpdateUserName();
  const [nameInput, setNameInput] = useState('');
  const [nameError, setNameError] = useState('');

  // Only render when requires_name is true
  if (!user?.requires_name) return null;

  const primaryColor = user.fsp?.primary_color || '#ED974F';

  const handleSubmit = async () => {
    const result = nameSchema.safeParse(nameInput);
    if (!result.success) {
      setNameError(result.error.issues[0].message);
      return;
    }
    setNameError('');
    try {
      await updateName.mutateAsync(result.data);
    } catch {
      setNameError('Failed to save name. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-6">
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-gray-900 text-center mb-1">
          {t('requireName.title')}
        </h2>
        <p className="text-sm text-gray-500 text-center mb-5">
          {t('requireName.subtitle')}
        </p>
        <input
          type="text"
          value={nameInput}
          onChange={(e) => {
            setNameInput(e.target.value);
            if (nameError) setNameError('');
          }}
          onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
          placeholder={t('requireName.placeholder')}
          className={`w-full rounded-xl border px-4 py-3 text-sm outline-none transition-colors ${
            nameError ? 'border-red-400' : 'border-gray-300'
          }`}
          autoFocus
        />
        {nameError && <p className="mt-1.5 text-xs text-red-500">{nameError}</p>}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={updateName.isPending}
          className="mt-4 w-full rounded-full py-3 text-sm font-semibold text-white uppercase tracking-wide disabled:opacity-50"
          style={{ backgroundColor: primaryColor }}
        >
          {updateName.isPending ? t('requireName.saving') : t('requireName.button')}
        </button>
      </div>
    </div>
  );
}
